import { GlobalContext } from "@context/GlobalContext";
import useAutenticar from "@hooks/useAutenticar";
import IconKtech from "@icons/IconKtech";
import EndPointServices from "@services/ApiServices";
import UtilServices from "@services/UtilServices";
import { Zap, UserPlus, User, Crown, Menu, X } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

export const Header = () => {
  const [state, dispatch] = useContext(GlobalContext);
  const { handleAutenticar, handleLogout } = useAutenticar();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    UtilServices.setStorage("idsistema_redirect", "2");
    handleLoginRedirect();
  }, []);

  async function handleLoginRedirect() {
    if (handleAutenticar()) {
      try {
        await EndPointServices.Autentica_VerificaSessaoSemError();
      } catch {
        handleLogout("/ConsultaIbscbs");
      }
    }
  }

  const getPlanBadgeStyle = (plan: string) => {
    switch (plan.toLowerCase()) {
      case "premium":
        return "bg-amber-500/20 text-amber-400 border-amber-500/30";
      default:
        return "bg-muted text-muted-foreground border-border-tail";
    }
  };
  const getPlanBadgeIconStyle = (plan: string) => {
    switch (plan.toLowerCase()) {
      case "premium":
        return "text-amber-400 border-amber-500/30";
      default:
        return "bg-muted text-muted-foreground border-border-tail";
    }
  };
  function scrollToSection(id: string) {
    const el = document.getElementById(id);
    if (!el) return;

    el.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  const navItems = [
    { label: "Como Funciona", href: "howitwork" },
    { label: "Planos", href: "plans" },
    { label: "Consulta IBS e CBS", href: "consult" },
  ];

  const navigate = useNavigate();
  return (
    <header className="w-full border-b border-border-tail/50 bg-background/80 backdrop-blur-xl sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <IconKtech
            width={45}
            height={45}
            onClick={() => {
              navigate("/ConsultaIbscbs");
            }}
          />
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6">
          {navItems.map((item) => (
            <a
              key={item.label}
              onClick={() => scrollToSection(item.href)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden sm:flex gap-2 items-center">
          <button
            className="flex items-center gap-2 px-4 py-2 bg-secondary-tail border-1 border-border-tail rounded-lg text-sm font-medium text-foreground hover:bg-secondary-tail/80 transition-all"
            onClick={() => scrollToSection("plans")}
          >
            <Zap className="w-4 h-4 text-primary-tail" />
            Upgrade
          </button>
          {Object.keys(state.dadosusuario).length === 0 && (
            <>
              <button
                className="flex items-center gap-2 px-4 py-2 bg-primary-tail border-1 border-border-tail rounded-lg text-sm font-medium text-foreground hover:bg-secondary-tail/80 hover:border-primary-tail/50 transition-all"
                onClick={() => navigate("/Cadastrar")}
              >
                <UserPlus className="w-4 h-4 text-foreground" />
                Cadastrar
              </button>
              <p
                onClick={() => {
                  navigate("/");
                }}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              >
                Entrar
              </p>
            </>
          )}

          {/* User Info */}
          {Object.keys(state.dadosusuario).length > 0 && (
            <>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-card border-1 border-border-tail rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-primary-tail/20 border-1 border-primary-tail/30 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary-tail" />
                  </div>
                  {/* <div className="hidden sm:block"> */}
                  <div>
                    <p className="text-sm font-medium text-foreground leading-tight">
                      {state.dadosusuario.nome}
                    </p>
                    <div className="flex items-center gap-1">
                      <Crown
                        className={`w-3 h-3 text-muted-foreground ${getPlanBadgeIconStyle(
                          state.dadosusuario?.planos?.find(
                            (p) => p.id_sistema === 3,
                          )?.plano || "Grátis",
                        )}`}
                      />
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded border-1 ${getPlanBadgeStyle(
                          state.dadosusuario?.planos?.find(
                            (p) => p.id_sistema === 3,
                          )?.plano || "Grátis",
                        )}`}
                      >
                        {state.dadosusuario?.planos?.find(
                          (p) => p.id_sistema === 3,
                        )?.plano || "Grátis"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <p
                onClick={() => {
                  handleLogout("/ConsultaIbscbs");
                }}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              >
                Sair
              </p>
            </>
          )}
        </div>

        <div className="sm:hidden">
          <Menu
            className="h-5 w-5 text-foreground cursor-pointer hover:text-primary-tail"
            onClick={() => {
              setIsOpen(true);
            }}
          />
        </div>
        {isOpen && (
          <div className="fixed z-50 gap-4 bg-background h-screen p-6 shadow-lg inset-y-0 right-0 h-full border-l border-border-tail sm:max-w-sm w-[280px] sm:w-[320px]">
            <div className="flex items-center justify-between">
              <IconKtech
                width={45}
                height={45}
                onClick={() => {
                  navigate("/ConsultaIbscbs");
                }}
              />
              <X
                className="h-5 w-5 text-foreground cursor-pointer hover:text-primary-tail"
                onClick={() => {
                  setIsOpen(false);
                }}
              />
            </div>
            <button
              className="flex items-center gap-2 px-4 py-2 bg-secondary-tail border-1 border-border-tail rounded-lg text-sm font-medium text-foreground hover:bg-secondary-tail/80 transition-all mt-4"
              onClick={() => {
                scrollToSection("plans");
                setIsOpen(false);
              }}
            >
              <Zap className="w-4 h-4 text-primary-tail" />
              Upgrade
            </button>
            {Object.keys(state.dadosusuario).length === 0 && (
              <>
                <button
                  className="flex items-center gap-2 px-4 py-2 bg-primary-tail border-1 border-border-tail rounded-lg text-sm font-medium text-foreground hover:bg-secondary-tail/80 hover:border-primary-tail/50 transition-all mt-2"
                  onClick={() => navigate("/Cadastrar")}
                >
                  <UserPlus className="w-4 h-4 text-foreground" />
                  Cadastrar
                </button>
              </>
            )}
            {/* User Info */}
            {Object.keys(state.dadosusuario).length > 0 && (
              <>
                <div className="flex items-center gap-3 mt-4">
                  <div className="flex items-center gap-2 px-3 py-2 bg-card border-1 border-border-tail rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-primary-tail/20 border-1 border-primary-tail/30 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary-tail" />
                    </div>
                    {/* <div className="hidden sm:block"> */}
                    <div>
                      <p className="text-sm font-medium text-foreground leading-tight">
                        {state.dadosusuario.nome}
                      </p>
                      <div className="flex items-center gap-1">
                        <Crown
                          className={`w-3 h-3 text-muted-foreground ${getPlanBadgeIconStyle(
                            state.dadosusuario?.planos?.find(
                              (p) => p.id_sistema === 3,
                            )?.plano || "Grátis",
                          )}`}
                        />
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded border-1 ${getPlanBadgeStyle(
                            state.dadosusuario?.planos?.find(
                              (p) => p.id_sistema === 3,
                            )?.plano || "Grátis",
                          )}`}
                        >
                          {state.dadosusuario?.planos?.find(
                            (p) => p.id_sistema === 3,
                          )?.plano || "Grátis"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            {/* Mobile Nav */}
            <nav className="flex flex-col gap-1 mt-6">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  onClick={() => {
                    scrollToSection(item.href);
                    setIsOpen(false);
                  }}
                  className="flex items-center px-2 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors cursor-pointer"
                >
                  {item.label}
                </a>
              ))}
            </nav>
            {Object.keys(state.dadosusuario).length > 0 && (
              <p
                onClick={() => {
                  handleLogout("/ConsultaIbscbs");
                  setIsOpen(false);
                }}
                className="text-sm px-2 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              >
                Sair
              </p>
            )}
            {Object.keys(state.dadosusuario).length === 0 && (
              <p
                onClick={() => {
                  navigate("/");
                  setIsOpen(false);
                }}
                className="px-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              >
                Entrar
              </p>
            )}
          </div>
        )}
      </div>
    </header>
  );
};
